import React, { useState } from 'react';
import {
  FileText,
  UserCheck,
  Building,
  CreditCard,
  Lock,
  Eye,
  AlertOctagon,
  ShieldAlert,
  Cpu,
  HelpCircle,
  Gavel,
  ChevronRight,
  ShieldCheck,
  CheckCircle2,
  RefreshCw,
  Scale,
  Ban
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'acceptance', title: '1. Acceptance & Eligibility', icon: FileText },
  { id: 'accounts', title: '2. User Accounts & Security', icon: Lock },
  { id: 'candidate-verification', title: '3. Candidate Verification', icon: UserCheck },
  { id: 'recruiter-verification', title: '4. Recruiter Verification', icon: Building },
  { id: 'subscriptions', title: '5. Subscriptions & Credits', icon: CreditCard },
  { id: 'payments', title: '6. Payments & Refund Policy', icon: ShieldCheck },
  { id: 'visibility-confidentiality', title: '7. Visibility & Confidentiality', icon: Eye },
  { id: 'anti-harvesting', title: '8. Anti-Poaching & Scraping', icon: Ban },
  { id: 'acceptable-use', title: '9. Acceptable Use Policy', icon: ShieldAlert },
  { id: 'ai-processing', title: '10. AI & Automated Matching', icon: Cpu },
  { id: 'disclaimers', title: '11. No Employment Guarantee', icon: AlertOctagon },
  { id: 'liability', title: '12. Limitation of Liability', icon: Scale },
  { id: 'termination', title: '13. Suspension & Termination', icon: RefreshCw },
  { id: 'governing-law', title: '14. Governing Law & Legal', icon: Gavel },
];

export const TermsAndConditions: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('acceptance');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Accent Gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                NEXGENT LEGAL GOVERNANCE
              </span>
              <span className="text-xs text-slate-500">v3.1 • Updated Aug 2026</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Terms of Service & Platform Governance
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <Gavel className="w-4 h-4 text-cyan-400" />
            <span>Jurisdiction: Pune, MH, India</span>
          </div>
        </div>
      </header>

      {/* Main Layout Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Legal Framework Index
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Legal Document Content */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-10">
          
          {/* Section 1 */}
          <section id="acceptance" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Acceptance of Terms & Eligibility</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              These Terms & Conditions ("Terms") govern your access to and use of <strong className="text-cyan-400">Nexgent</strong> (operated by Motherson Infotech). By registering, accessing, or utilizing the platform—whether as a Candidate, Recruiter, or Enterprise Administrator—you execute a legally binding agreement with Motherson Infotech.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="p-3.5 bg-slate-950/70 border border-slate-800 rounded-xl space-y-1">
                <div className="text-xs font-mono text-cyan-400">AGE REQUIREMENT (18+)</div>
                <div className="text-xs text-slate-300">You must be at least 18 years old and possess full legal authority to enter into binding digital contracts.</div>
              </div>
              <div className="p-3.5 bg-slate-950/70 border border-slate-800 rounded-xl space-y-1">
                <div className="text-xs font-mono text-cyan-400">PROFESSIONAL PURPOSE</div>
                <div className="text-xs text-slate-300">Nexgent is engineered strictly for executive recruitment, direct candidate matching, and leadership career placement.</div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="accounts" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. User Accounts & Security Responsibilities</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Account credentials must remain confidential. You are solely liable for all activities, actions, or API calls originating under your authenticated access key or account session.
            </p>
            <ul className="space-y-2 text-xs text-slate-400">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Multi-factor authentication (MFA) must be maintained when configured for high-level recruiter permissions.</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Immediate written notification to <strong>security@nexgent.tech</strong> is required upon detecting any credential compromise.</span>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="candidate-verification" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <UserCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. Candidate Registration & Verification Protocols</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Leadership profiles require absolute truthfulness. Candidates represent that all uploaded work histories, compensation figures, leadership accomplishments, and academic records are accurate.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <span className="text-cyan-400 font-semibold font-mono uppercase">Profile Audit Rights:</span>
              <p className="text-slate-400">
                Nexgent reserves the right to request proof of prior employment or credentials. Profiles determined to contain deliberate falsifications, misleading titles, or deceptive histories will face immediate termination without notice.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section id="recruiter-verification" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Building className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. Recruiter Verification & Corporate Legitimacy</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Recruiters must act on behalf of legitimate, legally registered corporate entities or verified search firms.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <div className="font-semibold text-white mb-1">Corporate Domain Check</div>
                <div className="text-slate-400">Registration via commercial domain emails (e.g., @company.com) is mandatory. Generic webmail domains are rejected.</div>
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <div className="font-semibold text-white mb-1">Verification Audits</div>
                <div className="text-slate-400">Nexgent may require corporate registration certificates (e.g., CIN, GST, or Tax IDs) before unlocking database credits.</div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="subscriptions" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CreditCard className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Subscription Plans & Credit Lifecycle</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Enterprise access features tiered plans (e.g., <em>Ignite</em>, <em>Accelerate</em>, <em>Dominate</em>). Access parameters and candidate contact reveals operate on a subscription credit model.
            </p>
            <div className="space-y-2 text-xs">
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800 flex justify-between items-center">
                <span className="text-white font-medium">Credit Allocation & Expiration</span>
                <span className="text-cyan-400 font-mono">Non-transferable / Valid for Billing Cycle</span>
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800 flex justify-between items-center">
                <span className="text-white font-medium">Pricing Adjustments</span>
                <span className="text-slate-400 font-mono">30-Day Prior Email Notice</span>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section id="payments" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Payment Gateway & Strict Refund Policy</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Payments are executed via PCI-DSS compliant third-party gateways (<strong className="text-white">Razorpay</strong>). All fees listed exclude applicable GST or statutory taxes unless explicitly stated.
            </p>
            <div className="p-4 bg-red-950/20 border border-red-900/40 rounded-xl space-y-1 text-xs">
              <div className="font-semibold text-red-400 uppercase font-mono">Strict No-Refund Condition:</div>
              <p className="text-slate-300">
                Subscription payments and credit package purchases are non-refundable once activated. Refunds are issued exclusively if a platform processing bug directly prevents credit distribution and remains unresolved beyond 7 business days.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section id="visibility-confidentiality" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Eye className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Confidential Executive Profiles & Visibility</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent utilizes reverse-matching protocols designed to protect active executive leadership from unauthorized exposure.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl text-xs space-y-2">
              <div className="text-cyan-400 font-semibold">Recruiter Confidentiality Obligations</div>
              <p className="text-slate-400 leading-relaxed">
                Recruiters accessing anonymized profiles or unmasked contact data agree to treat candidate information as strictly confidential. Candidate details must not be posted publicly, re-distributed outside the target hiring organization, or used for cold marketing outreach.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section id="anti-harvesting" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Ban className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. Anti-Poaching & Automated Scraping Rules</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Systematic extraction of executive talent profiles is illegal under these terms and triggers automated security lockouts.
            </p>
            <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-4 text-xs text-slate-400 space-y-2">
              <p className="text-white font-medium">Strictly Prohibited Activities:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Automated scraping, web crawling, or headless browser extraction of candidate databases.</li>
                <li>Bulk exporting candidate profiles to build internal external talent repositories.</li>
                <li>Feed integration into third-party AI LLMs or dataset aggregation models without prior license.</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section id="acceptable-use" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Acceptable Use Policy</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Users agree not to exploit vulnerabilities, attempt reverse engineering of compiled source code, or conduct spam communications using platform messaging channels.
            </p>
          </section>

          {/* Section 10 */}
          <section id="ai-processing" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Cpu className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">10. AI Processing & Automated Match Scoring</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Nexgent utilizes heuristic models and automated processing algorithms to calculate candidate relevance scores.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-400 space-y-1">
              <span className="text-cyan-400 font-semibold">Informational Matching Only:</span>
              <p>AI-calculated match percentages serve purely as decision-support metrics. Nexgent does not perform automated final hiring decisions. Recruiters retain total responsibility for interview evaluations.</p>
            </div>
          </section>

          {/* Section 11 */}
          <section id="disclaimers" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-cyan-800/40">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <AlertOctagon className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">11. No Employment Guarantee Disclaimer</h2>
            </div>
            <div className="p-4 bg-slate-950/90 border border-slate-800 rounded-xl text-xs space-y-2 text-slate-300">
              <p className="font-semibold text-cyan-400 uppercase font-mono">Platform Disclaimer:</p>
              <p className="leading-relaxed">
                Nexgent operates strictly as a technological match facilitator. Nexgent does <strong>not guarantee</strong> interviews, hiring outcomes, salary negotiations, job offers, or candidate performance quality. We accept zero liability for contractual disputes arising between employers and hired candidates.
              </p>
            </div>
          </section>

          {/* Section 12 */}
          <section id="liability" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Scale className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">12. Limitation of Liability</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              To the maximum extent permitted by applicable Indian law, Nexgent and Motherson Infotech shall not be liable for indirect, incidental, or consequential damages, lost revenue, or loss of career opportunity.
            </p>
            <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800 text-xs text-slate-400">
              <strong className="text-white">Liability Cap:</strong> In no event shall aggregate financial liability exceed the total subscription fees paid by the user to Nexgent in the <strong>12 months preceding the claim</strong>.
            </div>
          </section>

          {/* Section 13 */}
          <section id="termination" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <RefreshCw className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">13. Account Suspension & Service Termination</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              We reserve the right to suspend or terminate accounts immediately for terms violations, suspicious login patterns, fraudulent representation, or non-payment.
            </p>
          </section>

          {/* Section 14 */}
          <section id="governing-law" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Gavel className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">14. Governing Law & Dispute Resolution</h2>
                <p className="text-xs text-slate-400">Jurisdiction & Legal Representation</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              These Terms are governed by and construed in accordance with the laws of India. In the event of a dispute, parties shall attempt mutual resolution in good faith for 30 days prior to initiating formal arbitration under the Arbitration and Conciliation Act, 1996. The exclusive legal forum for all litigation shall be the courts of <strong className="text-cyan-400">Pune, Maharashtra, India</strong>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs pt-2">
              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">LEGAL ENTITY</div>
                <div className="text-sm font-bold text-white">Motherson Infotech</div>
                <div className="text-slate-400">Nexgent Division</div>
              </div>

              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">LEGAL COMMUNICATIONS</div>
                <div className="text-sm font-bold text-cyan-400">legal@nexgent.tech</div>
                <div className="text-slate-400">Support: support@nexgent.tech</div>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default TermsAndConditions;